import {Controller} from "stimulus";
import Rails from "@rails/ujs";
import * as Turbo from "@hotwired/turbo";

type FormInputElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const resolveForm = (event) => {
  if (event.target.form || event.currentTarget.form) {
    // An input (firing a change event) can specify a `form` attribute
    // see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-form
    return event.target.form || event.currentTarget.form;
  } else {
    // Find "closest" form by default
    return event.currentTarget.closest("form");
  }
};

// Narrow provided element type to HTMLButtonELement if truthy
function isButton(element: HTMLElement): element is HTMLButtonElement {
  return element.tagName.toLowerCase() == "button";
}
export default class SubmitFormController extends Controller<HTMLElement> {
  static targets = ["turboRefreshControl"];
  static values = {readOnly: Boolean, turboFrame: String};

  declare turboRefreshControlTarget?: HTMLElement;
  declare readOnlyValue: boolean;
  declare turboFrameValue: string;

  connect() {
    addEventListener("ajax:send", this.resetFormInputNames);
    addEventListener("turbo:submit-start", this.resetFormInputNames);
  }

  disconnect() {
    removeEventListener("ajax:send", this.resetFormInputNames);
    removeEventListener("turbo:submit-start", this.resetFormInputNames);
  }

  updateAction(event) {
    resolveForm(event).setAttribute("action", event.currentTarget.dataset.formAction);
  }

  submit(event) {
    this.addHiddenRefreshInput(event);

    const form = resolveForm(event);
    if (event.params.refresh) form.setAttribute("novalidate", "true");
    this.deactivateFormElements(form);
    form.requestSubmit();
  }

  // Safely submit a form remotely (even if it is not declared a "remote form")
  // (supports pagination - where we ajax in new content but still present the form as normal)
  remotely(event) {
    const form = resolveForm(event);

    if (event.params.refresh) this.addHiddenRefreshInput(event);

    // Emulate rails/ujs behaviour by including the submitter button
    if (isButton(this.element) && this.element.type == "submit") {
      Rails.setData(form, "ujs:submit-button", {name: this.element.name, value: this.element.value});
    }

    this.deactivateFormElements(form);

    if (form.dataset.remote) {
      Rails.fire(form, "submit");
    } else {
      form.setAttribute("data-remote", "true");
      Rails.fire(form, "submit");
      form.removeAttribute("data-remote");
    }
  }

  // Submits via Turbo's native submitForm
  // This is the same as used when `data-turbo` is enabled and provides the
  // `submitter` which has the beneit of respecting `formaction` and `formmethod`
  // overrides to the default form. We don't include the submitter if unless
  // formaction/method are supplied, as it appends an unwanted empty value to
  // the submitted form data.
  turbo(event) {
    const form = resolveForm(event);
    const submitter = event.currentTarget;

    if (event.params.preventEmptySubmit && submitter.value.trim() === "") {
      event.preventDefault();
      return;
    }

    if (event.params.refresh) this.addHiddenRefreshInput(event);

    this.deactivateFormElements(form);

    if (submitter.getAttribute("formaction") || submitter.getAttribute("formmethod")) {
      Turbo.navigator.submitForm(form, event.currentTarget);
    } else {
      Turbo.navigator.submitForm(form);
    }
  }

  turboRefresh(event) {
    const form = resolveForm(event);
    const methodInput = form.elements.namedItem("_method");
    const csrfInput = form.elements.namedItem("authenticity_token");

    // don't send CSRF tokens for GET requests
    if (csrfInput) { csrfInput.disabled = true; }

    // don't try to fake the method of the request
    if (methodInput) { methodInput.disabled = true; }

    if (this.turboFrameValue) {
      const turbo_frame = document.getElementById(this.turboFrameValue);

      // get the form data
      const data = new FormData(form);
      const url = new URL(`${form.action}/new`);
      const params = new URLSearchParams();
      data.forEach((value, key) => { if (typeof value === "string") params.append(key, value); });
      url.search = params.toString();

      // update the turbo frame src
      turbo_frame?.setAttribute("src", url.toString());

      // update the browser url
      history.replaceState({}, "", url);
    } else {
      // submit using the custom submit button
      // this was previously using form.requestSubmit(this.turboRefreshControlTarget),
      // however this did not respect the formmethod/formaction overrides in Safari
      this.turboRefreshControlTarget?.click();
    }

    // reset input state
    if (csrfInput) { csrfInput.disabled = false; }
    if (methodInput) { methodInput.disabled = false; }
  }

  // Prevent form submission if any elements are invalid
  validate(event) {
    const form = resolveForm(event);
    const invalidElements = [...form.elements].filter(element => !element.checkValidity());

    if (invalidElements.length) event.preventDefault();
    invalidElements[0].reportValidity();
  }

  // Update current URL query with existing form params.
  // This allows form values to be persisted across page reloads.
  updateUrlParams(event) {
    const form = resolveForm(event);
    const formData = new FormData(form);
    const currentUrl = new URL(window.location.href);
    const params = Object.keys(Object.fromEntries(formData)).filter(param => param !== "authenticity_token");

    params.forEach(param => {
      currentUrl.searchParams.delete(param);

      [...formData.getAll(param)]
        .filter(val => val != "") // Discard empty form params
        .forEach((val) => currentUrl.searchParams.append(param, val.toString()));
    });

    history.replaceState(null, "", currentUrl);
  }

  // This aims to prevent URLs from getting unnecessarily long.
  // Elements with no name are not added to the form request body
  disableEmptyInputs = (event) => {
    const form = resolveForm(event);

    Array.from(form.elements)
      .filter((element: FormInputElement) => ["INPUT", "SELECT", "TEXTAREA"].includes(element.tagName) && !element.value?.trim())
      .forEach((element: FormInputElement) => {
        if (!element.dataset.name) element.dataset.name = element.name;

        element.name = ""; // Prevents submission of form param

        // Reset after 1 second to handle form submission cancellation
        setTimeout(() => this.resetFormInputName(element), 1000);
      });
  };

  private addHiddenRefreshInput = (event) => {
    const form = resolveForm(event);

    if (!form.querySelector("input[name=refresh]")) {
      const refreshInput = document.createElement("input");
      refreshInput.type = "hidden";
      refreshInput.name = "refresh";
      refreshInput.value = "refresh";
      form.appendChild(refreshInput);
    }
  };

  private deactivateFormElements = (form: HTMLFormElement) => {
    if (!this.readOnlyValue) return;

    Array.from(form.elements).forEach(element => element.ariaReadOnly = "true");
  };

  private resetFormInputName = (element: FormInputElement) => {
    if (element.dataset.name) element.name = element.dataset.name;
  };

  private resetFormInputNames = () => {
    document.querySelectorAll<FormInputElement>("form input[data-name], form textarea[data-name]")
      .forEach((element) => this.resetFormInputName(element));
  };
}
